import { type DrivingSchoolLabel } from "../providers/drivingSchoolLabelsProvider";
import {
  Create,
  Edit,
  maxLength,
  required,
  Resource,
  SaveButton,
  SimpleForm,
  TextInput,
  Toolbar,
  ToolbarProps,
  useRedirect,
} from "react-admin";
import Box from "@mui/material/Box";
import { BackofficeRecordTitle } from "../misc/BackofficeRecordTitle";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { Column } from "../misc/Column";
import { useRecordAndSwitchDrivingSchoolIfNecessary } from "../hooks/useRecordAndSwitchDrivingSchoolIfNecessary";
import { SelectInput } from "../inputs/SelectInput";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { grants } from "../backoffice.access_control";
import { DeleteRecordButton } from "../buttons/DeleteRecordButton";
import { useCurrentDrivingSchoolId } from "../hooks/useCurrentDrivingSchoolId";
import { RecordDetailsHeader } from "../misc/RecordDetailsHeader";

function CreateDrivingSchoolLabel() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const redirect = useRedirect();

  if (!drivingSchoolId) {
    return (
      <Box className="centered">
        <Column sx={{ alignItems: "center" }}>Bitte wähle eine Fahrschule aus.</Column>
      </Box>
    );
  }

  return (
    <Create
      resource="labels"
      title={<BackofficeRecordTitle />}
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Label erfolgreich erstellt.");
          redirect(`/drivingSchools/${drivingSchoolId}/labels`);
        },
        onError: (error: Error) => {
          showErrorToast("Fehler beim Erstellen des Labels.");
          console.error("Failed to create drivingSchoolLabel.", error);
        },
        meta: { drivingSchoolId },
      }}
    >
      <DrivingSchoolLabelForm mode="create" />
    </Create>
  );
}

function EditDrivingSchoolLabel() {
  const label = useRecordAndSwitchDrivingSchoolIfNecessary<DrivingSchoolLabel>((it) => it.drivingSchoolId);
  const redirect = useRedirect();

  if (!label) {
    return <LoadingIndicator />;
  }

  return (
    <Edit
      resource="labels"
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Änderungen erfolgreich gespeichert.");
          redirect(`/drivingSchools/${label.drivingSchoolId}/labels`);
        },
        onError: (error: unknown) => {
          showErrorToast("Fehler beim Speichern.");
          console.error("Failed to update drivingSchoolLabel.", error);
        },
      }}
      mutationMode="pessimistic"
      title={
        <BackofficeRecordTitle
          header={
            <RecordDetailsHeader
              backLabel="Fahrschüler-Labels"
              backUrl={`/drivingSchools/${label.drivingSchoolId}/labels`}
              title={label.name}
            />
          }
        />
      }
    >
      <DrivingSchoolLabelForm mode="edit" />
    </Edit>
  );
}

function DrivingSchoolLabelForm({ mode }: { mode: "create" | "edit" }) {
  return (
    <SimpleForm warnWhenUnsavedChanges toolbar={<CustomToolbar />}>
      <TextInput label="Name" source="name" autoFocus={mode === "create"} validate={[required(), maxLength(20)]} />
      <TextInput label="Beschreibung" source="description" validate={required()} />
      <SelectInput
        label="Farbe"
        source="color"
        options={drivingSchoolLabelColorsOptions}
        validate={(value) => (value ? true : "Bitte wähle eine Farbe aus.")}
        size="small"
      />
    </SimpleForm>
  );
}

const CustomToolbar = (props: ToolbarProps) => {
  const drivingSchoolId = useCurrentDrivingSchoolId();
  return (
    <Toolbar {...props} sx={{ justifyContent: "space-between" }}>
      {grants.has("DrivingSchoolLabel:edit") && <SaveButton icon={<></>} />}
      {grants.has("DrivingSchoolLabel:delete") && (
        <DeleteRecordButton onSuccessRedirectTo={`/drivingSchools/${drivingSchoolId}/labels`} />
      )}
    </Toolbar>
  );
};

export const drivingSchoolLabelsResource = (
  <Resource
    key="labels"
    name="labels"
    create={CreateDrivingSchoolLabel}
    edit={EditDrivingSchoolLabel}
    options={{
      label: "Labels",
      createTitle: "Neues Label",
      noEntriesText: "Noch kein Label angelegt.",
      noMatchingEntriesText: "Keine passenden Labels gefunden.",
    }}
    recordRepresentation={(drivingSchoolLabel: DrivingSchoolLabel) => drivingSchoolLabel.name}
  />
);

const drivingSchoolLabelColorsOptions: [string, string][] = [
  ["greenUltraLight", "Grün"],
  ["greyUltraLight", "Grau"],
  ["orangeUltraLight", "Orange"],
  ["redUltraLight", "Rot"],
  ["violetUltraLight", "Violett"],
  ["blueUltraLight", "Blau"],
  ["yellowUltraLight", "Gelb"],
];
